import Q from 'q';
import { IHttpService, IHttpResponse } from 'Core';
import {
  IReportDataService,
  IBallTrajectoryService,
  IReportDataConversionService,
  ReportDataServiceHelper,
} from 'Services';
import {
  IOptimization,
  IReportOptimization,
  IDeviceStatus,
  IMultiGroupReport,
  IMeasurementReport,
  IDispersionEllipse,
  ICoordinate2D,
} from 'Data';

export class ReportDataService implements IReportDataService {
  private http: IHttpService;
  private ballTrajectoryService: IBallTrajectoryService;
  private dataConversionService: IReportDataConversionService;

  public static $inject = [
    'IHttpService',
    'IBallTrajectoryService',
    'IReportDataConversionService',
  ];

  constructor(
    http: IHttpService,
    ballTrajectoryService: IBallTrajectoryService,
    dataConversionService: IReportDataConversionService
  ) {
    this.http = http;
    this.ballTrajectoryService = ballTrajectoryService;
    this.dataConversionService = dataConversionService;
  }

  //uploadVideo(measurementId: string): Q.Promise<string> {
  //    var defered = Q.defer<string>();
  //    defered.resolve("success");
  //    return defered.promise;
  //}

  getReportData(): Q.Promise<[IMeasurementReport, boolean]> {
    return Q.all([this.loadReportNormalizedStrokes(), this.deviceOnline()]);
  }

  getOptimizationData(
    measurementId: Array<string>,
    optimizationType: string
  ): Q.Promise<IReportOptimization> {
    const defered = Q.defer<IReportOptimization>();
    let url = 'TestData/notexists.json';
    if (
      optimizationType === 'carry' ||
      optimizationType === 'total' ||
      optimizationType === 'combined'
    ) {
      url = 'TestData/driverOptimize.json';
    }
    if (
      optimizationType === 'low' ||
      optimizationType === 'high' ||
      optimizationType === 'medium'
    ) {
      url = 'TestData/ironOptimize.json';
    }

    this.http
      .get<IOptimization>(url)
      .then((response: IHttpResponse<IOptimization | string>) => {
        if (response.data) {
          let data: IOptimization;
          if (typeof response.data === 'string') {
            data = JSON.parse(response.data);
          } else {
            data = response.data as IOptimization;
          }

          defered.resolve(this.dataConversionService.convertOptimizedData(data));
        }
      })
      .fail(reason => {
        console.log(reason);
        defered.reject(null);
      })
      .done();
    return defered.promise;
  }

  private dispersionCacheRequest: Map<string, ICoordinate2D[]> = new Map<string, ICoordinate2D[]>();
  private dispersionCacheResponse: Map<string, IDispersionEllipse> = new Map<
    string,
    IDispersionEllipse
  >();

  getDispersionEllipse(
    coordinates: ICoordinate2D[],
    colorOfElllipsis: string
  ): Q.Promise<IDispersionEllipse> {
    const defered = Q.defer<IDispersionEllipse>();

    if (this.dispersionCacheResponse.get(colorOfElllipsis)) {
      if (
        ReportDataServiceHelper.compareDispersionCoordinates(
          this.dispersionCacheRequest.get(colorOfElllipsis) ?? [],
          coordinates
        )
      ) {
        defered.resolve(this.dispersionCacheResponse.get(colorOfElllipsis));
        return defered.promise;
      }
    }

    const url = 'https://test.mytrackman.com/api/dynamicreports/GetDispersionEllipse';
    const data = { Coordinates: coordinates };

    this.http
      .post<IDispersionEllipse>(url, data)
      .then((response: IHttpResponse<IDispersionEllipse | string>) => {
        const ellispe: IDispersionEllipse =
          (response.data as IDispersionEllipse) || JSON.parse(response.data as string);

        this.dispersionCacheRequest.set(colorOfElllipsis, coordinates);
        this.dispersionCacheResponse.set(colorOfElllipsis, ellispe);

        defered.resolve(ellispe);
      })
      .fail(reason => {
        console.log(reason);
        defered.reject(null);
      })
      .done();

    return defered.promise;
  }

  shortenUrl(url: string): Q.Promise<string> {
    const defered = Q.defer<string>();
    const data = { Url: url };
    const serviceUrl = 'https://test.mytrackman.com/api/DynamicReports/ShortenUrl'; //cross platform post!!
    this.http
      .post<string>(serviceUrl, data)
      .then((response: IHttpResponse<string>) => {
        defered.resolve(response.data);
      })
      .fail(() => {
        defered.reject(null);
      })
      .done();
    return defered.promise;
  }

  private loadReportNormalizedStrokes(): Q.Promise<IMeasurementReport> {
    const defered = Q.defer<IMeasurementReport>();

    const url = 'TestData/drShotAnalysisReport.json?tms=' + Date.now();

    //const url = 'TestData/multiGroupReport.json?tms=' + Date.now();
    //const url = "TestData/puttingMultiGroupReport.json?tms=" + Date.now();
    //const url = "TestData/singleGroupReport.json?tms=" + Date.now();
    //const url = 'TestData/puttingSingleGroupReport.json?tms=' + Date.now();

    //const url = "TestData/singleShotReport.json?tms=" + Date.now();
    //const url = "TestData/puttingSingleShotReport.json?tms=" + Date.now();

    //const url = "TestData/compareReport.json?tms=" + Date.now();
    //const url = "TestData/puttingCompareReport.json?tms=" + Date.now();

    //const url = 'TestData/testCenterReport.json?tms=' + Date.now();
    //const url = 'TestData/combineTestReport.json?tms=' + Date.now();

    this.http
      .get<IMultiGroupReport>(url)
      .then((response: IHttpResponse<IMultiGroupReport | string>) => {
        if (response.data) {
          let report: IMultiGroupReport;
          if (typeof response.data === 'string') {
            report = JSON.parse(response.data);
          } else {
            report = response.data as IMultiGroupReport;
          }
          defered.resolve(this.dataConversionService.convertData(report));
        }
      })
      .fail(reason => {
        console.log(reason);
        defered.reject(null);
      })
      .done();
    return defered.promise;
  }

  private deviceOnline(): Q.Promise<boolean> {
    const defered = Q.defer<boolean>();
    const url = 'TestData/Online.json';
    this.http
      .get<IDeviceStatus>(url)
      .then((response: IHttpResponse<IDeviceStatus | string>) => {
        if (response.data) {
          let json: IDeviceStatus;
          if (typeof response.data === 'string') {
            json = JSON.parse(response.data);
          } else {
            json = response.data;
          }
          defered.resolve(json.networkConnection.toLowerCase() === 'online');
        }
      })
      .fail(reason => {
        console.log(reason);
        defered.reject(null);
      })
      .done();
    return defered.promise;
  }
}
